// 🔹 Stylesheets
import "./PullToRefresh.css";

// 🔹 React Core
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// 🔹 Capacitor & Mobile
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";

// 🔹 Device tracking (a reload is not an app open / close)
import { markPageRefresh } from "../helpers/pageRefreshMarker";

// 🔹 UI Component Libraries
import { LoadingOutlined } from "@ant-design/icons";

/**
 * Checks if the current environment supports touch-based pull-to-refresh.
 * Active on Capacitor native platforms (iOS/Android mobile & tablet)
 * as well as mobile/tablet touch screens.
 */
export const isPullToRefreshSupported = (): boolean => {
  if (Capacitor.isNativePlatform()) return true;

  if (typeof window !== "undefined") {
    const hasTouch =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      Boolean((navigator as any).msMaxTouchPoints);
    const isMobileOrTablet = window.innerWidth <= 1200;
    return Boolean(hasTouch && isMobileOrTablet);
  }

  return false;
};

/**
 * Helper to check whether window or ANY parent container of target
 * is currently scrolled down. Pull to refresh must ONLY initiate
 * when all scrollable ancestor containers are at the top (scrollTop <= 0).
 */
const isElementOrParentScrolled = (target: Node | null): boolean => {
  if (typeof window === "undefined") return false;

  // 1. Check window / document scroll
  const winScroll =
    window.scrollY ||
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  if (winScroll > 0) return true;

  // 2. Check all ancestor DOM elements up to document.body
  let el: HTMLElement | null =
    target instanceof HTMLElement ? target : target?.parentElement || null;

  while (el && el !== document.body && el !== document.documentElement) {
    if (el.scrollTop > 0) {
      return true;
    }
    const style = window.getComputedStyle(el);
    if (
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      el.scrollTop > 0
    ) {
      return true;
    }
    el = el.parentElement;
  }

  return false;
};

/**
 * Helper to check if a touch occurred on an element that shouldn't trigger
 * pull-to-refresh (e.g. dropdowns/selects, virtual lists, signature canvas, sliders, active modals).
 */
const shouldIgnoreTouch = (target: Node | null): boolean => {
  if (!target || !(target instanceof Element)) return false;

  // Exclude touches originating inside any dropdown, select, picker, menu, modal, drawer, form input, or editor
  const ignoredSelector = [
    // Dropdowns & Selects (including Ant Design portal popups and virtual lists)
    ".ant-select-dropdown",
    ".ant-dropdown",
    ".ant-picker-dropdown",
    ".ant-cascader-dropdown",
    ".ant-tree-select-dropdown",
    ".ant-popover",
    ".ant-tooltip",
    ".rc-virtual-list",
    ".rc-virtual-list-holder",
    ".rc-virtual-list-holder-inner",
    ".ant-select",
    ".ant-select-selector",
    ".ant-select-item",
    ".ant-picker",
    ".ant-menu",
    ".ant-menu-submenu",
    ".dropdown",
    ".dropdown-menu",
    ".filterCommonDropdown",
    ".whatsBannedDropdown",
    // Modals & Drawers
    ".ant-modal",
    ".ant-modal-content",
    ".ant-modal-body",
    ".ant-modal-wrap",
    ".ant-drawer",
    ".ant-drawer-content",
    ".ant-drawer-body",
    // Form Inputs & Editors
    "input",
    "textarea",
    "select",
    ".form-select",
    ".form-control",
    ".ck-editor",
    ".ck-editor__editable",
    // Special interactive controls
    "canvas",
    ".signature-canvas",
    ".react-signature-canvas",
    ".ant-slider",
    ".swiper",
    ".no-pull-to-refresh",
    ".no-ptr",
    "[data-no-ptr]",
  ].join(", ");

  if (target.closest(ignoredSelector)) {
    return true;
  }

  return false;
};

export interface PullToRefreshProps {
  /**
   * Async action to run when the user pulls past the threshold.
   * If not provided, defaults to window.location.reload() for a full page refresh.
   */
  onRefresh?: () => Promise<any> | void;
  /**
   * Pull distance (in px) required to trigger a refresh.
   * @default 70
   */
  threshold?: number;
  /**
   * Maximum visually displayed pull distance (in px).
   * @default 115
   */
  maxPull?: number;
  /**
   * Set to `false` to hard-disable pull-to-refresh.
   */
  enabled?: boolean;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Reusable native-feel pull-to-refresh component.
 *
 * Can be used in two ways:
 * 1. As a local wrapper around specific content:
 *    <PullToRefresh onRefresh={fetchData}>{content}</PullToRefresh>
 *
 * 2. As a global handler at the root of the app (App.tsx):
 *    <GlobalPullToRefresh />
 */
export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  threshold = 70,
  maxPull = 115,
  enabled,
  children,
  className,
}) => {
  const isSupported = isPullToRefreshSupported();
  const active = enabled !== false && isSupported;

  const containerRef = useRef<HTMLDivElement>(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const stateRef = useRef({ pullDistance: 0, refreshing: false });
  stateRef.current.pullDistance = pullDistance;
  stateRef.current.refreshing = refreshing;

  useEffect(() => {
    if (!active) return;

    let startY: number | null = null;
    let startX: number | null = null;
    let touchTarget: Node | null = null;
    let pulling = false;
    let hasTriggeredThresholdHaptic = false;

    const onTouchStart = (e: TouchEvent) => {
      if (stateRef.current.refreshing) return;
      if (e.touches.length !== 1) return;

      const target = e.target as Node;

      // If wrapping local children, restrict to container; otherwise global
      if (
        containerRef.current &&
        children &&
        !containerRef.current.contains(target)
      ) {
        return;
      }

      if (shouldIgnoreTouch(target)) return;
      if (isElementOrParentScrolled(target)) return;

      startY = e.touches[0]?.clientY ?? null;
      startX = e.touches[0]?.clientX ?? null;
      touchTarget = target;
      pulling = false;
      hasTriggeredThresholdHaptic = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (stateRef.current.refreshing) return;
      if (startY === null || startX === null) return;

      // Abort if touch is inside or on an ignored element (e.g. dropdown open)
      if (shouldIgnoreTouch(touchTarget) || shouldIgnoreTouch(e.target as Node)) {
        if (pulling) {
          pulling = false;
          setPullDistance(0);
        }
        return;
      }

      // Double check scroll position - cancel if user has scrolled
      if (isElementOrParentScrolled(touchTarget) || isElementOrParentScrolled(e.target as Node)) {
        if (pulling) {
          pulling = false;
          setPullDistance(0);
        }
        return;
      }

      const currentY = e.touches[0]?.clientY ?? startY;
      const currentX = e.touches[0]?.clientX ?? startX;
      const deltaY = currentY - startY;
      const deltaX = currentX - startX;

      // Let horizontal swipes take priority (e.g. horizontal tables, tabs, carousels)
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (pulling) {
          pulling = false;
          setPullDistance(0);
        }
        return;
      }

      // Ignore upward movements / tiny jitters
      if (deltaY <= 6) {
        if (pulling) {
          pulling = false;
          setPullDistance(0);
        }
        return;
      }

      pulling = true;

      // Damped pull distance formula for smooth native physical feel
      const damped = Math.min(
        maxPull,
        Math.round(Math.pow(deltaY, 0.85) * 1.5)
      );
      setPullDistance(damped);

      // Light haptic feedback once threshold is crossed
      if (damped >= threshold && !hasTriggeredThresholdHaptic) {
        hasTriggeredThresholdHaptic = true;
        try {
          Haptics.impact({ style: ImpactStyle.Light });
        } catch (err) {}
      } else if (damped < threshold && hasTriggeredThresholdHaptic) {
        hasTriggeredThresholdHaptic = false;
      }

      // Prevent native browser overscroll/pull conflicts
      if (pulling && e.cancelable) {
        e.preventDefault();
      }
    };

    const finishPull = () => {
      if (stateRef.current.refreshing) return;
      if (!pulling) {
        setPullDistance(0);
        return;
      }

      pulling = false;
      const distance = stateRef.current.pullDistance;
      setPullDistance(0);

      if (distance >= threshold) {
        setRefreshing(true);
        try {
          Haptics.impact({ style: ImpactStyle.Medium });
        } catch (err) {}

        const executeRefresh = async () => {
          if (onRefreshRef.current) {
            await onRefreshRef.current();
          } else {
            // Default global action: brief visual feedback followed by page reload
            await new Promise((resolve) => setTimeout(resolve, 250));
            // A reload replaces the DOCUMENT, not the process: the session continues.
            // Tell device tracking so neither the `pagehide` it listens for nor the
            // reloaded document's own boot is read as an app close / app open.
            markPageRefresh();
            window.location.reload();
          }
        };

        Promise.resolve()
          .then(executeRefresh)
          .catch((err) => {
            console.error("Pull-to-refresh execution error:", err);
          })
          .finally(() => {
            setRefreshing(false);
          });
      }
    };

    const cancelPull = () => {
      pulling = false;
      setPullDistance(0);
    };

    document.addEventListener("touchstart", onTouchStart, {
      passive: true,
    } as any);
    document.addEventListener("touchmove", onTouchMove, {
      passive: false,
    } as any);
    document.addEventListener("touchend", finishPull, { passive: true } as any);
    document.addEventListener("touchcancel", cancelPull, {
      passive: true,
    } as any);

    const prevOverscroll = document.documentElement.style.overscrollBehaviorY;
    document.documentElement.style.overscrollBehaviorY = "contain";

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", finishPull);
      document.removeEventListener("touchcancel", cancelPull);
      document.documentElement.style.overscrollBehaviorY = prevOverscroll;
    };
  }, [active, threshold, maxPull, children]);

  const showIndicator = active && (pullDistance > 0 || refreshing);
  const progress = Math.min(1, pullDistance / threshold);
  // Indicator smoothly slides down from the top
  const indicatorOffset = refreshing
    ? 20
    : Math.max(0, pullDistance - threshold * 0.4);

  const indicatorScale = refreshing
    ? 1
    : Math.min(1.08, 0.7 + progress * 0.38);

  const indicatorElement = active && showIndicator && (
    <div
      className={`ptr-layer${showIndicator ? " ptr-layer--visible" : ""}`}
      style={{
        transform: `translateY(${indicatorOffset}px)`,
      }}
      aria-hidden={!showIndicator}
    >
      <div
        className={`ptr-circle-btn${
          pullDistance >= threshold ? " ptr-circle-btn--ready" : ""
        }${refreshing ? " ptr-circle-btn--refreshing" : ""}`}
        style={{
          transform: `scale(${indicatorScale})`,
        }}
      >
        {refreshing ? (
          <svg
            className="ptr-browser-spinner"
            viewBox="0 0 50 50"
            width="28"
            height="28"
          >
            <circle
              className="ptr-browser-spinner-path"
              cx="25"
              cy="25"
              r="18"
              fill="none"
              strokeWidth="4"
            />
          </svg>
        ) : (
          <svg
            className="ptr-browser-arrow"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            style={{
              transform: `rotate(${progress * 360}deg)`,
            }}
          >
            <path
              fill="currentColor"
              d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            />
          </svg>
        )}
      </div>
    </div>
  );

  // If used without children (global mode)
  if (!children) {
    if (typeof document !== "undefined" && indicatorElement) {
      return ReactDOM.createPortal(indicatorElement, document.body);
    }
    return null;
  }

  // If used as a container wrapper
  return (
    <div
      ref={containerRef}
      className={`pull-to-refresh${active ? " pull-to-refresh--active" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      {indicatorElement}
      {children}
    </div>
  );
};

/**
 * Convenient global Pull-to-Refresh component to be mounted once in App.tsx.
 * Provides application-wide pull-to-refresh on mobile and tablet devices.
 */
export const GlobalPullToRefresh: React.FC<
  Omit<PullToRefreshProps, "children">
> = (props) => {
  return <PullToRefresh {...props} />;
};

export default PullToRefresh;
